/**
 * Capa de MEDIA / EVIDENCIA RICA (hito M2 · "quien captura el audio captura
 * la verdad").
 *
 * Desde el Bolsillo un participante levanta evidencia en territorio: graba
 * audio, adjunta una foto o un archivo. La subida es SIEMPRE proxiada por la
 * API (MinIO es interno a Docker; el navegador no lo alcanza — nada de URLs
 * prefirmadas): mandamos el multipart a /actividades/{id}/aportes/media y la
 * API guarda el objeto en el bucket 'agora-media' y crea un Aporte INSERT-only
 * (C1). Si es audio, la API encola la transcripción en el worker del host.
 *
 * LECTURA autenticada: GET /media/{aporte_id} exige Bearer (auth: miembro del
 * proceso), así que un <img src> / <audio src> directo NO sirve — el token vive
 * en memoria, no en cookie. Por eso descargamos el objeto como Blob con el
 * mismo Bearer + refresh de lib/api y creamos un object URL local
 * ({@link useMediaUrl}) para reproducir/ver/descargar.
 *
 * OFFLINE: si la subida falla por RED, la UI del Bolsillo la reintenta con el
 * MISMO token `idem` (idempotencia de contrato: el backend deduplica por
 * payload.idem y no duplica el aporte). No persistimos el Blob a localStorage
 * (tamaño); el reintento vive mientras la pantalla siga abierta.
 */
import { useEffect, useRef, useState } from 'react';
import { API_BASE, apiFetch, apiDownload } from './api';
import { nuevoIdem } from './colaOffline';
import type { Aporte, AporteMediaTipo } from './sesion';

/** Tipo de evidencia que se sube (coincide con el enum de Aporte de media). */
export type MediaTipo = AporteMediaTipo;

/** Metadatos mínimos del blob que viajan en el multipart. */
export interface MediaMeta {
  /** Nombre de archivo legible (p. ej. "nota-de-voz.webm", "foto.jpg"). */
  filename: string;
  /** MIME real del blob (p. ej. "audio/webm", "image/jpeg"). */
  mime: string;
}

/**
 * Sube un blob de evidencia (audio/foto/archivo) a una actividad. Devuelve el
 * Aporte creado (o el ya existente si el backend deduplicó por `idem`).
 *
 * Lanza ApiError; en particular ApiError.status === 0 significa fallo de RED
 * (sin conexión): quien llama debe encolar y reintentar con el MISMO `idem`.
 */
export async function subirMedia(
  actividadId: string,
  blob: Blob,
  tipo: MediaTipo,
  meta: MediaMeta,
  idem: string = nuevoIdem(),
  extra?: {
    /**
     * Payload estructurado que viaja JUNTO al archivo (ADR-0005 §5): la
     * maqueta y su propuesta de valor son UN aporte citable. El backend lo
     * valida contra la captura declarada por el manifiesto.
     */
    campos?: Record<string, unknown>;
    /** Aporte de la misma actividad al que se encadena la evidencia (C1). */
    parentId?: string;
    /**
     * Grabación abierta de la sala (solo facilitador): el worker la procesa
     * con diarización y cada intervención nace como aporte citable.
     */
    microfonoSala?: boolean;
    /** ISO-8601 del instante en que arrancó la grabación (ancla absoluta). */
    inicioGrabacion?: string;
    /**
     * ISO-8601 del final de la grabación. Con la cola durable un audio puede
     * subirse mucho después de grabarse: sin estos sellos, la hora del aporte
     * (que es la del envío) haría creer que se dijo cuando solo se pudo mandar.
     */
    finGrabacion?: string;
    /** Personas en la mesa (1-32): mejora la identificación de hablantes. */
    numHablantes?: number;
  },
): Promise<Aporte> {
  const form = new FormData();
  // El backend espera el campo 'file' + 'tipo' + 'idem' (contrato de media).
  form.append('file', blob, meta.filename);
  form.append('tipo', tipo);
  form.append('idem', idem);
  if (extra?.campos && Object.keys(extra.campos).length > 0) {
    form.append('campos', JSON.stringify(extra.campos));
  }
  if (extra?.parentId) form.append('parent_id', extra.parentId);
  if (extra?.microfonoSala) form.append('microfono_sala', 'true');
  if (extra?.inicioGrabacion) form.append('inicio_grabacion', extra.inicioGrabacion);
  if (extra?.finGrabacion) form.append('fin_grabacion', extra.finGrabacion);
  if (extra?.numHablantes) form.append('num_hablantes', String(extra.numHablantes));

  return apiFetch<Aporte>(`/actividades/${actividadId}/aportes/media`, {
    method: 'POST',
    form,
  });
}

/**
 * Ruta cruda del objeto en la API (GET /media/{id}). Ojo: requiere Bearer, así
 * que sirve para diagnóstico o como href de respaldo, NO como `src` directo de
 * <img>/<audio> (esos no llevan el header). Para reproducir usá {@link useMediaUrl}.
 */
export function urlMedia(aporteId: string): string {
  return `${API_BASE}/media/${aporteId}`;
}

/** Descarga el objeto como Blob con el Bearer + refresh de lib/api. */
export function descargarMedia(aporteId: string): Promise<Blob> {
  return apiDownload(`/media/${aporteId}`);
}

/** Estado de carga de un object URL de media. */
export interface MediaUrlState {
  /** object URL listo para <audio>/<img>/<a>, o null mientras carga/falla. */
  url: string | null;
  cargando: boolean;
  error: string | null;
}

/**
 * Descarga un objeto de media autenticado y expone un object URL estable,
 * revocándolo al desmontar o al cambiar de aporte (sin fugas de memoria).
 * Pasar `null` no dispara ninguna descarga (útil para render condicional).
 */
export function useMediaUrl(aporteId: string | null | undefined): MediaUrlState {
  const [estado, setEstado] = useState<MediaUrlState>({
    url: null,
    cargando: Boolean(aporteId),
    error: null,
  });
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let vivo = true;
    // Revoca el object URL anterior antes de pedir el nuevo.
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }

    if (!aporteId) {
      setEstado({ url: null, cargando: false, error: null });
      return;
    }

    setEstado({ url: null, cargando: true, error: null });
    descargarMedia(aporteId)
      .then((blob) => {
        if (!vivo) return;
        const objUrl = URL.createObjectURL(blob);
        urlRef.current = objUrl;
        setEstado({ url: objUrl, cargando: false, error: null });
      })
      .catch((err: unknown) => {
        if (!vivo) return;
        const msg =
          err && typeof err === 'object' && 'detail' in err
            ? String((err as { detail: unknown }).detail)
            : 'No pudimos cargar la evidencia.';
        setEstado({ url: null, cargando: false, error: msg });
      });

    return () => {
      vivo = false;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [aporteId]);

  return estado;
}
